/*
 * Postgres 컬럼이 snake_case 라 행 → 도메인 변환 지점에서만 규칙을 끈다
 * (변환 이후 도메인 타입은 전부 camelCase — matching.api 와 같은 이유·같은 방식).
 */
/* eslint-disable @typescript-eslint/naming-convention */
import { getAuthedClient, supabase } from '~/common/utils/supabase.util.ts';
import { getMyProfileId } from '~/features/profile/api/profile.api';

import type { DogClass, DogClassInput } from '~/features/catalog/types/catalog.type';

/**
 * 카탈로그 어댑터 — `classes` 테이블.
 *
 * @description 목록 조회는 **로그인 없이** 한다. 홈·검색·클래스 상세는 둘러보기 화면이라 anon 키로도
 *  읽혀야 하며, 마이그레이션 0011 이 anon 에게 `is_published` 인 행 + 표시용 컬럼만 열어 두었다.
 *
 *  개설·수정은 지도사 본인 것만 가능하다. 그 판정은 여기(앱)가 아니라 RLS 가 한다 —
 *  `tutor_id = 내 프로필` 인 행만 통과하므로, 앱이 실수로 남의 클래스 id 를 보내도 DB 가 막는다.
 */

interface ClassRow {
  id: string;
  slug: string;
  tutor_id: string;
  title: string;
  summary: string;
  description: string;
  curriculum: string[] | null;
  duration_label: string;
  price: number;
  emoji: string | null;
  gradient: string | null;
  is_trial: boolean;
  is_published: boolean;
}

/** 지도사가 새 클래스를 만들 때의 기본 카드 배경 — 시안의 카드 톤을 유지하기 위한 폴백. */
const DEFAULT_GRADIENT = 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)';
const DEFAULT_EMOJI = '🐾';

/** 표시용 컬럼만 고른다 — `select('*')` 는 anon 에게 없는 컬럼까지 요청해 권한 오류가 난다. */
const CLASS_COLUMNS =
  'id, slug, tutor_id, title, summary, description, curriculum, duration_label, price, emoji, gradient, is_trial, is_published';

function toDogClass(row: ClassRow): DogClass {
  return {
    dbId: row.id,
    id: row.slug,
    tutorId: row.tutor_id,
    title: row.title,
    summary: row.summary,
    description: row.description,
    curriculum: row.curriculum ?? [],
    durationLabel: row.duration_label,
    price: row.price,
    emoji: row.emoji ?? DEFAULT_EMOJI,
    gradient: row.gradient ?? DEFAULT_GRADIENT,
    isTrial: row.is_trial,
    isPublished: row.is_published,
  };
}

/** 공개 목록 — 홈·검색·상세가 쓴다. */
export async function fetchClassesApi(): Promise<DogClass[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('classes')
    .select(CLASS_COLUMNS)
    .eq('is_published', true)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return ((data as unknown as ClassRow[] | null) ?? []).map(toDogClass);
}

/**
 * 내 클래스 목록 — 지도사 관리 화면.
 * @description 비공개 클래스도 포함한다. 여기서 빼면 한 번 내린 클래스를 다시 올릴 방법이 사라진다.
 */
export async function fetchMyClassesApi(): Promise<DogClass[]> {
  const client = await getAuthedClient();
  if (!client) return [];
  const profileId = await getMyProfileId();
  if (!profileId) return [];

  const { data, error } = await client
    .from('classes')
    .select(CLASS_COLUMNS)
    .eq('tutor_id', profileId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return ((data as unknown as ClassRow[] | null) ?? []).map(toDogClass);
}

function toRow(input: DogClassInput) {
  return {
    slug: input.slug.trim(),
    title: input.title.trim(),
    summary: input.summary.trim(),
    description: input.description.trim(),
    // 빈 줄은 목록의 빈 항목이 되므로 저장 단계에서 걸러낸다.
    curriculum: input.curriculum.map(item => item.trim()).filter(Boolean),
    duration_label: input.durationLabel.trim(),
    price: input.price,
    is_trial: input.isTrial,
    is_published: input.isPublished,
  };
}

export async function createClassApi(input: DogClassInput): Promise<DogClass> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');
  const profileId = await getMyProfileId();
  if (!profileId) throw new Error('먼저 지도사 프로필을 등록해주세요.');

  const { data, error } = await client
    .from('classes')
    .insert({
      ...toRow(input),
      tutor_id: profileId,
      emoji: DEFAULT_EMOJI,
      gradient: DEFAULT_GRADIENT,
    })
    .select(CLASS_COLUMNS)
    .single();

  if (error) {
    // 슬러그는 공개 주소라 전역 유일하다 — 충돌 원인을 그대로 알려야 사용자가 고칠 수 있다.
    if (error.code === '23505')
      throw new Error('이미 사용 중인 주소입니다. 다른 값을 입력해주세요.');
    throw error;
  }
  return toDogClass(data as unknown as ClassRow);
}

export async function updateClassApi(dbId: string, input: DogClassInput): Promise<DogClass> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');

  const { data, error } = await client
    .from('classes')
    .update(toRow(input))
    .eq('id', dbId)
    .select(CLASS_COLUMNS)
    .single();

  if (error) {
    if (error.code === '23505')
      throw new Error('이미 사용 중인 주소입니다. 다른 값을 입력해주세요.');
    throw error;
  }
  return toDogClass(data as unknown as ClassRow);
}

/**
 * 공개/비공개 전환.
 * @description 목록에서 바로 내리고 올릴 수 있어야 한다 — 수정 화면까지 들어가야만 내릴 수 있으면
 *  "일단 내려놓기"가 번거로워 지도사가 대신 클래스를 지우게 된다(결제 이력이 설명 불가능해진다).
 */
export async function setClassPublishedApi(dbId: string, isPublished: boolean): Promise<DogClass> {
  const client = await getAuthedClient();
  if (!client) throw new Error('Supabase 세션을 만들 수 없습니다.');

  const { data, error } = await client
    .from('classes')
    .update({ is_published: isPublished })
    .eq('id', dbId)
    .select(CLASS_COLUMNS)
    .single();

  if (error) throw error;
  return toDogClass(data as unknown as ClassRow);
}
